/**
 * Secure Logger
 * 
 * Provides logging with automatic redaction of sensitive data.
 * Only logs in development mode to prevent information disclosure.
 */

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;

// Fields to redact from logs
const SENSITIVE_FIELDS = [
    'password',
    'passwordConfirm',
    'email',
    'auth_user_id',
    'telefono',
    'whatsapp',
    'cuit',
    'api_key',
    'token',
    'secret',
    'authorization'
];

/**
 * Redact sensitive fields from object
 */
function redactSensitive(data: any): any {
    if (!data || typeof data !== 'object') return data;

    if (Array.isArray(data)) {
        return data.map(item => redactSensitive(item));
    }

    const redacted: any = {};

    for (const [key, value] of Object.entries(data)) {
        const lowerKey = key.toLowerCase();
        const isSensitive = SENSITIVE_FIELDS.some(field =>
            lowerKey.includes(field.toLowerCase())
        );

        if (isSensitive) {
            redacted[key] = '[REDACTED]';
        } else if (typeof value === 'object') {
            redacted[key] = redactSensitive(value);
        } else {
            redacted[key] = value;
        }
    }

    return redacted;
}

/**
 * Format log message with timestamp
 */
function formatMessage(level: string, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level}] ${message}`;
}

export const Logger = {
    /**
     * Debug logging (only in development)
     */
    debug(message: string, data?: any): void {
        if (isDev) {
            const safeData = data ? redactSensitive(data) : undefined;
            console.log(formatMessage('DEBUG', message), safeData);
        }
    },

    /**
     * Info logging
     */
    info(message: string, data?: any): void {
        if (isDev) {
            const safeData = data ? redactSensitive(data) : undefined;
            console.info(formatMessage('INFO', message), safeData);
        }
    },

    /**
     * Warning logging
     */
    warn(message: string, data?: any): void {
        const safeData = data ? redactSensitive(data) : undefined;
        console.warn(formatMessage('WARN', message), safeData);
    },

    /**
     * Error logging (always enabled, but redacted)
     */
    error(message: string, error?: any): void {
        console.error(formatMessage('ERROR', message));

        if (error) {
            if (isDev) {
                // Full error in dev
                console.error(error);
            } else {
                // Minimal error in prod
                const safeError: any = {
                    message: error?.message || 'Unknown error',
                    code: error?.code,
                    name: error?.name
                };
                console.error(safeError);
            }
        }
    },

    /**
     * Success logging
     */
    success(message: string, data?: any): void {
        if (isDev) {
            const safeData = data ? redactSensitive(data) : undefined;
            console.log(formatMessage('SUCCESS', `✅ ${message}`), safeData);
        }
    }
};
